import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    Link,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Collapse,
    IconButton,
    useTheme,
    alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DevicesIcon from '@mui/icons-material/Devices';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getAllFp1Devices } from '../fp1.api';
import { Fp1Device, Fp1OrgSubscriptionTier } from '../fp1.types';
import { getPodOrgCoverage } from '../../pods/pods.api';

interface Props {
    /** Switch to the Devices subtab pre-filtered to this organization. */
    onViewOrgDevices: (orgUuid: string, orgName: string) => void;
    /**
     * Switch to the Devices subtab pre-filtered to one org's athlete-Transferred pods with
     * the given subscription status ('active_athlete' or 'past_due').
     */
    onViewOrgAthleteSubs: (
        orgUuid: string,
        orgName: string,
        status: 'active_athlete' | 'past_due',
    ) => void;
}

// ── Per-org aggregate ────────────────────────────────────────────────────────
interface OrgPods {
    uuid: string;
    name: string;
    tier: Fp1OrgSubscriptionTier | null;
    stripeId: string | null;
    total: number;
    activePaid: number;
    comp: number;
    invoiced: number;
    unpaid: number;
    /** Distinct athletes with a Transferred pod from this org whose own subscription is active. */
    activeAthletes: number;
    /** Distinct athletes with a Transferred pod from this org whose own subscription is past due. */
    pastDueAthletes: number;
}

// ── Sorting ───────────────────────────────────────────────────────────────────
type SortKey = 'total' | 'activePaid' | 'comp' | 'invoiced' | 'unpaid';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
    { key: 'total', label: 'Total pods' },
    { key: 'activePaid', label: 'Active paid pods' },
    { key: 'comp', label: "Comp'd pods" },
    { key: 'invoiced', label: 'Invoiced pods' },
    { key: 'unpaid', label: 'Unpaid pods' },
];

const fmt = (n: number) => n.toLocaleString('en-US');

// A pod that has been transferred out to an athlete is no longer an org pod.
const isAthletePod = (d: Fp1Device): boolean =>
    d.subscription_status === 'active_athlete' ||
    d.subscription_holder === 'athlete' ||
    !!(d.athlete_uuid && d.athlete_uuid.trim());

// A "Transferred" pod: the athlete holds (pays) their own subscription. Used to count the
// distinct transferred athletes per org, split by whether that subscription is active vs
// past due. Assigned pods (org still pays) are excluded — only real transfers count here.
const isTransferredAthletePod = (d: Fp1Device): boolean =>
    d.subscription_holder === 'athlete' && !!(d.athlete_uuid && d.athlete_uuid.trim());

// ── Tier presentation ─────────────────────────────────────────────────────────
const tierStyle = (theme: Theme, tier: Fp1OrgSubscriptionTier | null) => {
    switch (tier) {
        case 'gold':
            return { label: 'Gold', color: '#B8860B', bg: alpha('#D4AF37', 0.16) };
        case 'silver':
            return { label: 'Silver', color: '#6E7378', bg: alpha('#9AA0A6', 0.18) };
        case 'bronze':
            return { label: 'Bronze', color: '#A0673B', bg: alpha('#CD7F32', 0.16) };
        default:
            return {
                label: 'No tier',
                color: theme.palette.text.secondary,
                bg: theme.palette.action.hover,
            };
    }
};

// ── Per-bucket stat cell ────────────────────────────────────────────────────
const Stat: React.FC<{ value: number; label: string; color: string }> = ({
    value,
    label,
    color,
}) => (
    <Box
        sx={{
            flex: 1,
            minWidth: 76,
            px: 1,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: alpha(color, 0.08),
        }}
    >
        <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', lineHeight: 1.1, color }}>
            {fmt(value)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
    </Box>
);

// ── Expandable athlete-subscription row (drills into the Devices subtab) ──────
const AthleteSubRow: React.FC<{
    label: string;
    value: number;
    color: string;
    onClick: () => void;
}> = ({ label, value, color, onClick }) => (
    <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
            }
        }}
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1,
            py: 0.75,
            borderRadius: 1.5,
            cursor: 'pointer',
            bgcolor: alpha(color, 0.06),
            transition: 'background-color 120ms',
            '&:hover': { bgcolor: alpha(color, 0.16) },
        }}
        title="View these devices"
    >
        <Typography variant="body2" sx={{ fontWeight: 600, color }}>
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 800, color }}>{fmt(value)}</Typography>
            <ChevronRightIcon sx={{ fontSize: '1.1rem', color }} />
        </Box>
    </Box>
);

// ── Org card (own expand/collapse state) ─────────────────────────────────────
const OrgPodCard: React.FC<{
    o: OrgPods;
    onViewOrgDevices: Props['onViewOrgDevices'];
    onViewOrgAthleteSubs: Props['onViewOrgAthleteSubs'];
}> = ({ o, onViewOrgDevices, onViewOrgAthleteSubs }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const ts = tierStyle(theme, o.tier);

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column' }}
        >
            {/* Org name + tier + total */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}
                        noWrap
                        title={o.name}
                    >
                        {o.name}
                    </Typography>
                    <Chip
                        size="small"
                        label={ts.label}
                        sx={{
                            mt: 0.75,
                            height: 22,
                            fontWeight: 700,
                            color: ts.color,
                            bgcolor: ts.bg,
                        }}
                    />
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.75rem', lineHeight: 1 }}>
                        {fmt(o.total)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        total pods
                    </Typography>
                </Box>
            </Box>

            {/* Bucket breakdown */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.75, flexWrap: 'wrap' }}>
                <Stat value={o.activePaid} label="Active paid" color={theme.palette.success.main} />
                <Stat value={o.comp} label="Comp'd" color={theme.palette.info.main} />
                <Stat value={o.invoiced} label="Invoiced" color={theme.palette.secondary.main} />
                <Stat value={o.unpaid} label="Unpaid" color={theme.palette.error.main} />
            </Box>

            {/* Links */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2.5,
                    mt: 1.75,
                    pt: 1.25,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                {o.stripeId ? (
                    <Link
                        href={`https://dashboard.stripe.com/customers/${o.stripeId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600,
                            fontSize: '0.85rem',
                        }}
                    >
                        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                        Stripe
                    </Link>
                ) : (
                    <Tooltip title="No Stripe customer on file for this org">
                        <Typography
                            variant="body2"
                            color="text.disabled"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontWeight: 600,
                            }}
                        >
                            <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                            Stripe
                        </Typography>
                    </Tooltip>
                )}
                <Link
                    component="button"
                    type="button"
                    onClick={() => onViewOrgDevices(o.uuid, o.name)}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                    }}
                >
                    <DevicesIcon sx={{ fontSize: '1rem' }} />
                    View devices
                </Link>
            </Box>

            {/* Athlete transfers — expand/collapse */}
            <Box
                role="button"
                tabIndex={0}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen((v) => !v);
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1.5,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 700,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                    }}
                >
                    Athlete transfers
                </Typography>
                <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'}>
                    {open ? (
                        <ExpandLessIcon fontSize="small" />
                    ) : (
                        <ExpandMoreIcon fontSize="small" />
                    )}
                </IconButton>
            </Box>
            <Collapse in={open} mountOnEnter unmountOnExit>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                    <AthleteSubRow
                        label="Active Athlete Subscriptions"
                        value={o.activeAthletes}
                        color={theme.palette.success.main}
                        onClick={() => onViewOrgAthleteSubs(o.uuid, o.name, 'active_athlete')}
                    />
                    <AthleteSubRow
                        label="Past Due Athlete Subscriptions"
                        value={o.pastDueAthletes}
                        color={theme.palette.error.main}
                        onClick={() => onViewOrgAthleteSubs(o.uuid, o.name, 'past_due')}
                    />
                </Box>
            </Collapse>
        </Paper>
    );
};

// ── Module-level caches — re-opening the subtab paints instantly, then refreshes.
let fleetCache: Fp1Device[] | null = null;
let stripeIdCache: Record<string, string> | null = null;

/**
 * Map org uuid → Stripe customer id from the pod org-coverage endpoint. This is the
 * same pod-scoped source the /admin/pods dashboard uses for its Stripe links, so it
 * resolves a `cus_…` for every org that holds pods (the heavy org analytics dashboard
 * was unreliable here).
 */
const fetchOrgStripeIds = async (): Promise<Record<string, string>> => {
    const map: Record<string, string> = {};
    const res = await getPodOrgCoverage();
    (res.data?.data ?? []).forEach((row) => {
        if (row.organization_uuid && row.stripe_customer_id) {
            map[row.organization_uuid.toLowerCase()] = row.stripe_customer_id;
        }
    });
    return map;
};

const Fp1OrgPodsTab: React.FC<Props> = ({ onViewOrgDevices, onViewOrgAthleteSubs }) => {
    const [devices, setDevices] = useState<Fp1Device[]>(() => fleetCache ?? []);
    const [stripeIds, setStripeIds] = useState<Record<string, string>>(() => stripeIdCache ?? {});
    const [loading, setLoading] = useState(fleetCache === null);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('total');

    useEffect(() => {
        let cancelled = false;
        const hasCache = fleetCache !== null;
        if (hasCache) setRefreshing(true);
        else setLoading(true);
        setError(null);

        // Stripe ids are a best-effort enrichment — never block the fleet on them.
        const stripePromise = fetchOrgStripeIds().catch((err) => {
            console.error('Failed to load org Stripe ids', err);
            return stripeIdCache ?? {};
        });

        Promise.all([getAllFp1Devices(), stripePromise])
            .then(([all, ids]) => {
                fleetCache = all;
                stripeIdCache = ids;
                if (!cancelled) {
                    setDevices(all);
                    setStripeIds(ids);
                }
            })
            .catch((err) => {
                console.error('Failed to load pod fleet for org distribution', err);
                if (!cancelled && !hasCache) {
                    setError('Failed to load pods. Please try again.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                    setRefreshing(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const orgs = useMemo<OrgPods[]>(() => {
        interface Tally {
            uuid: string;
            name: string;
            tier: Fp1OrgSubscriptionTier | null;
            activeOrg: number;
            partial: number;
            comp: number;
            invoiced: number;
            pastDue: number;
            noSub: number;
            seats: number;
            // Distinct transferred athletes (by athlete uuid), split by subscription state.
            activeAthleteSet: Set<string>;
            pastDueAthleteSet: Set<string>;
        }
        const byOrg = new Map<string, Tally>();

        const getTally = (orgUuid: string, d: Fp1Device): Tally => {
            const key = orgUuid.toLowerCase();
            let t = byOrg.get(key);
            if (!t) {
                t = {
                    uuid: orgUuid,
                    name: d.organization_name || d.assigned_org?.name || 'Unknown organization',
                    tier: null,
                    activeOrg: 0,
                    partial: 0,
                    comp: 0,
                    invoiced: 0,
                    pastDue: 0,
                    noSub: 0,
                    seats: 0,
                    activeAthleteSet: new Set<string>(),
                    pastDueAthleteSet: new Set<string>(),
                };
                byOrg.set(key, t);
            }
            return t;
        };

        for (const d of devices) {
            const orgUuid = d.organization_uuid?.trim();
            if (!orgUuid) continue;
            if (d.status !== 'deployed') continue;

            const t = getTally(orgUuid, d);

            // Transferred (athlete-held) pod: the athlete pays their own subscription. Count
            // the distinct athletes by active vs past due; these never count toward the org's
            // own pod buckets below.
            if (isTransferredAthletePod(d)) {
                const athleteKey = (d.athlete_uuid ?? '').trim();
                if (athleteKey) {
                    if (d.subscription_status === 'active_athlete') {
                        t.activeAthleteSet.add(athleteKey);
                    } else if (d.subscription_status === 'past_due') {
                        t.pastDueAthleteSet.add(athleteKey);
                    }
                }
                continue;
            }

            // Any other athlete-assigned pod (org still pays) isn't an org-owned pod either.
            if (isAthletePod(d)) continue;

            // ── Org-owned pod buckets ──
            const tier = (d.assigned_org?.tier ?? null) as Fp1OrgSubscriptionTier | null;
            if (tier && !t.tier) t.tier = tier;
            // Subscription seats the org actually pays for (same across its pods).
            const seats = d.org_subscription_quantity ?? 0;
            if (seats > t.seats) t.seats = seats;

            switch (d.subscription_status) {
                case 'comp':
                    t.comp += 1;
                    break;
                case 'invoiced':
                    t.invoiced += 1;
                    break;
                case 'active_org':
                    t.activeOrg += 1;
                    break;
                case 'partial_org':
                    t.partial += 1;
                    break;
                case 'past_due':
                    t.pastDue += 1;
                    break;
                default:
                    // 'no_sub' and any other deployed-but-unbilled state.
                    t.noSub += 1;
                    break;
            }
        }

        return Array.from(byOrg.values())
            .map((t) => {
                // At an oversold org the first `seats` partial pods are covered (paid);
                // the overage is unpaid. A fully-covered org has no partial pods.
                const coveredPartial = Math.min(t.partial, t.seats);
                const activePaid = t.activeOrg + coveredPartial;
                const unpaid = t.pastDue + t.noSub + (t.partial - coveredPartial);
                const total = activePaid + t.comp + t.invoiced + unpaid;
                return {
                    uuid: t.uuid,
                    name: t.name,
                    tier: t.tier,
                    stripeId: stripeIds[t.uuid.toLowerCase()] ?? null,
                    total,
                    activePaid,
                    comp: t.comp,
                    invoiced: t.invoiced,
                    unpaid,
                    activeAthletes: t.activeAthleteSet.size,
                    pastDueAthletes: t.pastDueAthleteSet.size,
                } as OrgPods;
            })
            .filter((o) => o.total > 0)
            .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
    }, [devices, stripeIds]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matched = q ? orgs.filter((o) => o.name.toLowerCase().includes(q)) : orgs;
        // Sort by the selected metric (desc), tie-break by name.
        return [...matched].sort((a, b) => b[sortKey] - a[sortKey] || a.name.localeCompare(b.name));
    }, [orgs, query, sortKey]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 8,
                    gap: 2,
                }}
            >
                <CircularProgress />
                <Typography color="text.secondary">Loading org pods…</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    const totalPods = orgs.reduce((s, o) => s + o.total, 0);

    return (
        <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 0.5, sm: 1 }, py: 1 }}>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        Pods by Organization
                    </Typography>
                    {refreshing && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <CircularProgress size={14} thickness={5} />
                            <Typography variant="caption" color="text.secondary">
                                Refreshing…
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {fmt(orgs.length)} organizations · {fmt(totalPods)} org pods deployed (excludes
                    pods transferred to athletes). Expand a card for its athlete transfers.
                </Typography>
            </Box>

            {/* Search + sort */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search organizations…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{ flexGrow: 1, maxWidth: 360 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="org-pods-sort-label">Sort by</InputLabel>
                    <Select
                        labelId="org-pods-sort-label"
                        label="Sort by"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value as SortKey)}
                    >
                        {SORT_OPTIONS.map((opt) => (
                            <MenuItem key={opt.key} value={opt.key}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {visible.length === 0 ? (
                <Alert severity="info">
                    {orgs.length === 0
                        ? 'No organizations with deployed pods found.'
                        : 'No organizations match your search.'}
                </Alert>
            ) : (
                // Scroll container — vertical scrollbar on the right keeps all org boxes reachable.
                <Box
                    sx={{
                        maxHeight: 'calc(100vh - 300px)',
                        overflowY: 'auto',
                        pr: 1,
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, 1fr)',
                        },
                        gap: 2,
                        alignContent: 'start',
                    }}
                >
                    {visible.map((o) => (
                        <OrgPodCard
                            key={o.uuid}
                            o={o}
                            onViewOrgDevices={onViewOrgDevices}
                            onViewOrgAthleteSubs={onViewOrgAthleteSubs}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default Fp1OrgPodsTab;
